import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { API_PREFIX, apiFetch, apiGet, type Envelope, type Page } from '@/api/client'

export interface LocationFix {
  lat: number
  lng: number
}

export interface VisitRead {
  id: string
  visit_number: string
  user_id: string
  customer_id: string
  planned_at: string | null
  checked_in_at: string | null
  check_in_lat: number | null
  check_in_lng: number | null
  geofence_radius_m: number
  check_in_within_fence: boolean | null
  checked_out_at: string | null
  check_out_lat: number | null
  check_out_lng: number | null
  notes: string | null
  outcome: string | null
  photo_key: string | null
  signature_data: string | null
  order_id: string | null
  status: 'planned' | 'in_progress' | 'completed' | 'cancelled'
  created_at: string
  updated_at: string
}

export function useVisit(visitId: string) {
  return useQuery({
    queryKey: ['field_visits', visitId],
    queryFn: async () => {
      // Assuming a GET endpoint exists or will exist. The PRD/checklist focuses on creation/update,
      // but a read endpoint is standard. For now, we'll fetch from the list endpoint if GET /id doesn't exist,
      // or we can implement GET /id on backend. Let's assume GET /id exists or we'll add it.
      return apiGet<VisitRead>(`${API_PREFIX}/field-visits/${visitId}`)
    },
  })
}

export function useVisits(params?: { date_from?: string; date_to?: string; status?: string }) {
  return useQuery({
    queryKey: ['field_visits', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams()
      if (params?.date_from) searchParams.set('date_from', params.date_from)
      if (params?.date_to) searchParams.set('date_to', params.date_to)
      if (params?.status) searchParams.set('status', params.status)
      const qs = searchParams.toString()
      return apiFetch<Page<VisitRead>>(`${API_PREFIX}/field-visits${qs ? `?${qs}` : ''}`)
    },
  })
}

export function useCheckInVisit() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ visitId, fix }: { visitId: string; fix: LocationFix }) => {
      const response = await apiFetch<Envelope<VisitRead>>(`${API_PREFIX}/field-visits/${visitId}/check-in`, {
        method: 'POST',
        body: JSON.stringify(fix),
      })
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['field_visits', data.id], data)
    },
  })
}

export function useCheckOutVisit() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ visitId, fix }: { visitId: string; fix: LocationFix }) => {
      const response = await apiFetch<Envelope<VisitRead>>(`${API_PREFIX}/field-visits/${visitId}/check-out`, {
        method: 'POST',
        body: JSON.stringify(fix),
      })
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['field_visits', data.id], data)
    },
  })
}

export function useUpdateVisit() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ visitId, data }: { visitId: string; data: { notes?: string; outcome?: string; photo_key?: string; signature_data?: string; order_id?: string } }) => {
      const response = await apiFetch<Envelope<VisitRead>>(`${API_PREFIX}/field-visits/${visitId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      })
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['field_visits', data.id], data)
    },
  })
}

export interface UploadTicketRead {
  key: string
  url: string
  method: string
  content_type: string
  max_bytes: number
  expires_at: number
  fields: Record<string, string>
}

export function usePhotoTicket() {
  return useMutation({
    mutationFn: async ({ visitId }: { visitId: string }) => {
      const response = await apiFetch<Envelope<UploadTicketRead>>(`${API_PREFIX}/field-visits/${visitId}/photo-ticket`, {
        method: 'POST',
      })
      return response.data
    },
  })
}
